import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';

// Define types for the new API
interface PlacePrediction {
  placeId: string;
  mainText: {
    text: string;
    matches: Array<{
      startOffset: number;
      endOffset: number;
    }>;
  };
  secondaryText?: {
    text: string;
    matches: Array<{
      startOffset: number;
      endOffset: number;
    }>;
  };
  text: {
    text: string;
    matches: Array<{
      startOffset: number;
      endOffset: number;
    }>;
  };
  types: string[];
  distanceMeters?: number;
}

interface LocationSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const textStyles = {
  // Base text styles
  base: {
    fontFamily: 'var(--chat-font-family)',
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-normal)',
    lineHeight: 'var(--chat-line-height)',
    color: 'var(--planner-text-primary)'
  },
  // Header styles
  h2: {
    fontSize: 'var(--planner-heading-size)',
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  h3: {
    fontSize: 'var(--planner-subheading-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  h4: {
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  // Text sizes
  large: {
    fontSize: 'var(--planner-subheading-size)'
  },
  small: {
    fontSize: 'var(--planner-small-size)'
  },
  xs: {
    fontSize: 'calc(var(--chat-font-size) * 0.833)' // 10px if base is 12px
  },
  // Font weights
  medium: {
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  semibold: {
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  // Colors
  secondary: {
    color: 'var(--planner-text-secondary)'
  },
  tertiary: {
    color: 'var(--planner-text-tertiary)'
  }
};

export function LocationSearch({ value, onChange, placeholder = 'Where to?' }: LocationSearchProps) {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  
  // Store the library reference, NOT individual classes
  const placesLibraryRef = useRef<any>(null);

  // Initialize Google Places API
  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        if (window.google?.maps?.places) {
          const places = await google.maps.importLibrary("places");
          placesLibraryRef.current = places;
          sessionToken.current = new (places as any).AutocompleteSessionToken();
          console.log('[LocationSearch] Google Places initialized');
        } else {
          if (!document.getElementById('google-maps-script')) {
            const script = document.createElement('script');
            script.id = 'google-maps-script';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_PLACES_API_KEY}&libraries=places&v=weekly`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
            
            script.onload = async () => {
              const places = await google.maps.importLibrary("places");
              placesLibraryRef.current = places;
              sessionToken.current = new (places as any).AutocompleteSessionToken();
              console.log('[LocationSearch] Google Places loaded and initialized');
            };
          }
        }
      } catch (error) {
        console.error('[LocationSearch] Error initializing Google Places:', error);
      }
    };

    initializeGoogleMaps();
  }, []);

  const getPlacePredictions = async (input: string) => {
    console.log('[LocationSearch] Getting predictions for:', input);
    
    if (!placesLibraryRef.current || input.length < 2) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);

    try {
      const request = {
        input,
        sessionToken: sessionToken.current,
        includedPrimaryTypes: ['locality', 'administrative_area_level_1', 'country'],
        language: 'en',
      };

      console.log('[LocationSearch] Fetching suggestions...');
      
      // Call the static method on the class from the library
      const { AutocompleteSuggestion } = placesLibraryRef.current;
      const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      
      console.log('[LocationSearch] Response:', response);
      
      if (response.suggestions && response.suggestions.length > 0) {
        const placePredictions = response.suggestions
          .filter((suggestion: any) => suggestion.placePrediction)
          .map((suggestion: any) => suggestion.placePrediction);
        
        setPredictions(placePredictions);
        setIsOpen(true);
      } else {
        setPredictions([]);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('[LocationSearch] Error fetching predictions:', error);
      setPredictions([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Rest of your component remains the same...
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };
  
  const debouncedGetPredictions = useRef(
    debounce((input: string) => getPlacePredictions(input), 300)
  ).current;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    console.log('[LocationSearch] Input changed:', newValue);
    setInputValue(newValue);
    
    if (newValue.length >= 2) {
      debouncedGetPredictions(newValue);
    } else {
      setPredictions([]);
      setIsOpen(false);
    }
  };

  const handlePredictionSelect = (prediction: PlacePrediction) => {
    // Use the full text from the prediction
    const selectedText = prediction.text.text;
    setInputValue(selectedText);
    onChange(selectedText);
    setIsOpen(false);
    setPredictions([]);
    
    // Create a new session token after selection
    /* if (AutocompleteSessionToken) {
      sessionToken.current = new AutocompleteSessionToken();
    } */
  };

  // Rest of the component remains mostly the same...
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && predictions.length > 0) {
      e.preventDefault();
      handlePredictionSelect(predictions[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current && 
        dropdownRef.current && 
        !inputRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onChange(inputValue);
  };

  return (
    <form onSubmit={handleFormSubmit} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 2 && debouncedGetPredictions(inputValue)}
          placeholder={placeholder}
          className="w-full px-4 py-2 pl-8 border border-gray-300 rounded-full shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          autoComplete="off"
          style={textStyles.base}
        />
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          style={{ 
            width: 'var(--planner-icon-size-sm)', 
            height: 'var(--planner-icon-size-sm)' 
          }}
        />
      </div>

      {/* Predictions dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
        >
          {isLoading ? (
            <div className="px-4 py-3 text-center" style={textStyles.base}>
              <div className="inline-block h-4 w-4 border-2 border-t-transparent border-gray-300 rounded-full animate-spin mr-2" />
              Loading suggestions...
            </div>
          ) : predictions.length > 0 ? (
            <ul className="py-1 divide-y divide-gray-100">
              {predictions.map((prediction) => (
                <li
                  key={prediction.placeId}
                  onClick={() => handlePredictionSelect(prediction)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex flex-col gap-1">
                    <div className="font-medium" style={textStyles.base}>
                      {prediction.mainText?.text || prediction.text.text}
                    </div>
                    {prediction.secondaryText && (
                      <div style={{ ...textStyles.base, ...textStyles.secondary }}>
                        {prediction.secondaryText.text}
                      </div>
                    )}
                    {prediction.distanceMeters && (
                      <div style={{ ...textStyles.base, ...textStyles.small, ...textStyles.tertiary }}>
                        {(prediction.distanceMeters / 1000).toFixed(1)} km away
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : inputValue.length >= 2 ? (
            <div className="px-4 py-3 text-center" style={textStyles.base}>
              No results found
            </div>
          ) : null}
        </div>
      )}
    </form>
  );
}